// @ts-nocheck
import { useState, forwardRef, Ref } from 'react';
import { PurpleSelectTrigger } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/PurpleSelectTrigger';
import { StyledPurpleSelectWrapper } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/styles';
import { PLACEHOLDER_SELECT_AN_EVENT, TEST_IDS } from 'modules/predictiveGoals/constants';
import curry from 'lodash/curry';
import { BasicFilterSelect } from 'ui/selects/FilterSelect';
import { pxToRem } from 'utils/convert';
import type { BaseOption } from 'ui/actionMenu/types';
import { Close16 } from 'ui/icons/action/Close16';
import { colors } from 'ui/theme/colors';
import { Popover, PopoverContent } from 'ui/popover';
import { UpdatedOrConditionalBlock } from 'modules/predictiveGoals/components/queryBuilder/components/pgQueryBuilderEnhancements/LeadingOrSubsequentBlock';
import { TemplateEventPopoverContent } from 'modules/predictiveGoals/types';
import {
  QueryBaseContainer,
  QueryCloseButton,
  RightCol,
  RowFlexSpaceBetween,
  StyledDisplayConditions,
  TemplateEventPopoverContainer,
  TemplateEventPopoverTrigger,
  StyledSecondaryContentText,
  TemplatePopoverContentContainer,
  TemplatePopoverContentResult,
  UpdatedLeftCol,
  UpdatedMidColWithPurchaseEvent,
} from '../Styled';
import { EventCondition } from './EventCondition';
import { CONDITION_TYPE_TO_LEFT_COL_TEXT, PURCHASE_CONDITION_QUERY_KEY } from '../constants';
import { createEventCondition } from '../utils/queryHelpers';
import { getComponentForCondition } from '../utils';

export const createUpdateCondition = curry(
  (onUpdate, { eventConditions = [], ...restQuery }, conditionKey, nextQuery) => {
    const newQuery = {
      ...restQuery,
      eventConditions: eventConditions.map((condition) => {
        if (condition.queryKey !== conditionKey) {
          return condition;
        }

        return {
          ...condition,
          query: {
            ...nextQuery,
          },
        };
      }),
    };
    newQuery[conditionKey] = nextQuery;
    onUpdate(newQuery);
  },
);
const TemplateEventPopoverTriggerContent = forwardRef(
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  ({ children, isOpen, ...props }, forwardedRef: Ref<HTMLButtonElement>) => {
    return (
      <div ref={forwardedRef} {...props}>
        {children}
      </div>
    );
  },
);

export const shouldShowTemplatePopover = (
  templatePopoverContent: TemplateEventPopoverContent,
  andQueryIdx: number,
  orQueryIdx: number,
) => {
  // False if content was not created at all
  if (!templatePopoverContent.eventName || templatePopoverContent.eventName === '') {
    return false;
  }
  // False if this event iteration isn't the first one
  if (andQueryIdx !== 0 || orQueryIdx !== 0) {
    return false;
  }
  return true;
};

export type EventBaseProps = {
  andQueryIdx: number;
  orQueryIdx: number;
  /** Action fired when the delete icon is clicked */
  deleteAction: (e: React.MouseEvent) => void;
  /** Default value for the event dropdown */
  event: string;
  /** Options for the event dropdown */
  events: BaseOption[];
  /** Identifier for the component */
  id: string;
  /** The string to be shown on left side of form fields */
  leftColText: string;
  /** Setter for the property dropdown input change */
  setEvent: () => void;
  templatePopoverContent: TemplateEventPopoverContent;
};
export const EventBase = ({
  events,
  id,
  onDelete,
  onUpdate,
  query = {},
  andQueryIdx,
  orQueryIdx,
  templatePopoverContent,
}: EventBaseProps) => {
  const { name, window, eventConditions = [] } = query;
  const defaultEvent = events?.filter((event) => event.value === name)?.[0];
  const [conditionProps, setConditionProps] = useState(defaultEvent?.conditionProps || {});
  const [popoverOpen, togglePopoverOpen] = useState(false);

  const updateEvent = (eventName) => {
    const selectedEvent = events?.filter((event) => event.value === eventName)?.[0];
    const { conditionProps: selectedEventConditionProps, type: eventType } = selectedEvent || {};
    const nextConditions = createEventCondition(eventType, query);
    const updateParams = {
      name: eventName,
      type: eventType,
      window,
      eventConditions: nextConditions,
    };

    onUpdate(updateParams);
    setConditionProps(selectedEventConditionProps || {});
  };

  const updateCondition = createUpdateCondition(onUpdate, query);

  const displayedEventConditions = eventConditions.map(({ queryKey, query: queryData }) => {
    const ConditionInput = getComponentForCondition(queryKey);

    return queryKey !== PURCHASE_CONDITION_QUERY_KEY ? (
      <ConditionInput
        key={queryKey}
        {...conditionProps}
        onUpdate={updateCondition(queryKey)}
        id={queryKey}
        query={queryData}
      />
    ) : (
      <EventCondition
        leftColText={CONDITION_TYPE_TO_LEFT_COL_TEXT[queryKey]}
        id={queryKey}
        key={queryKey}
      >
        <ConditionInput
          {...conditionProps}
          onUpdate={updateCondition(queryKey)}
          id={queryKey}
          query={queryData}
        />
      </EventCondition>
    );
  });

  return (
    <QueryBaseContainer data-test={`${id}_EventBase`}>
      {
        <>
          <UpdatedLeftCol>
            <UpdatedOrConditionalBlock index={orQueryIdx} type={'event'} />
          </UpdatedLeftCol>
          <UpdatedMidColWithPurchaseEvent>
            <RowFlexSpaceBetween>
              {
                <>
                  <Popover
                    defaultOpen={shouldShowTemplatePopover(
                      templatePopoverContent,
                      andQueryIdx,
                      orQueryIdx,
                    )}
                    onOpenChange={(toggleStatus: boolean) => {
                      if (toggleStatus) {
                        return;
                      }
                      togglePopoverOpen(toggleStatus);
                    }}
                  >
                    <TemplateEventPopoverContainer>
                      <TemplateEventPopoverTrigger isOpen={popoverOpen} asChild>
                        <TemplateEventPopoverTriggerContent />
                      </TemplateEventPopoverTrigger>
                      <PopoverContent
                        side="top"
                        onOpenAutoFocus={(e: Event) => {
                          // Prevents auto focus of content to prevent blue element outline
                          e.preventDefault();
                        }}
                      >
                        <TemplatePopoverContentContainer>
                          <TemplatePopoverContentResult>
                            {templatePopoverContent.result}
                          </TemplatePopoverContentResult>
                          <StyledSecondaryContentText>
                            {templatePopoverContent.action}
                          </StyledSecondaryContentText>
                        </TemplatePopoverContentContainer>
                      </PopoverContent>
                      <BasicFilterSelect
                        width={`${pxToRem(400)}`}
                        defaultValues={name ? [name] : []}
                        id={`${id}_EventBaseEventSelector`}
                        options={events}
                        placeholder={PLACEHOLDER_SELECT_AN_EVENT}
                        onSelect={([eventName]) => eventName && updateEvent(eventName)}
                        inputContent={{
                          filterPlaceholder: 'Search events',
                        }}
                        SelectWrapper={StyledPurpleSelectWrapper}
                        Trigger={PurpleSelectTrigger}
                      />
                    </TemplateEventPopoverContainer>
                  </Popover>
                </>
              }
            </RowFlexSpaceBetween>
            {name && <StyledDisplayConditions>{displayedEventConditions}</StyledDisplayConditions>}
          </UpdatedMidColWithPurchaseEvent>
        </>
      }

      {
        <RightCol>
          <QueryCloseButton
            onClick={onDelete}
            data-test={TEST_IDS.DeleteEventButton}
            color={colors.secondaryText}
          >
            <Close16 />
          </QueryCloseButton>
        </RightCol>
      }
    </QueryBaseContainer>
  );
};
